import { Alert, Flex, Loader, Typography } from '@percent/lemonade'
import { Trans, useTranslation } from 'react-i18next'
import { GrantProgramCard } from './grantProgramCard/GrantProgramCard'
import styles from './GrantsPage.module.scss'
import { ErrorState } from './components/errorState/ErrorState'
import { EmptyState } from './components/emptyState/EmptyState'
import { useAuthState } from '@percent/cause-dashboard/common/hooks'
import { useLocale } from '@percent/cause-dashboard/common/hooks/useLocale/useLocale'
import { useQuery } from '@percent/utility'
import { useServices } from '@percent/cause-dashboard/context/serviceContext/ServiceContext'
import { useBreadcrumbs } from '../layout/breadcrumbsContext/useBreadcrumbs/useBreadcrumbs'
import { selectAuthState } from '@percent/cause-dashboard/context/auth/authReducer/authReducer'
import { HELP_LINK } from '@percent/cause-dashboard/constants/support'
import { useEffect } from 'react'
import { RoutePath } from '../routes/Routes'
import { useNavigate } from 'react-router-dom'

export const GrantsPage = () => {
  const { t } = useTranslation()
  useBreadcrumbs([{ label: t('breadcrumb.grants') }])
  const { authState } = useAuthState()
  const { claimAccepted } = selectAuthState(authState)
  const { grantsService } = useServices()
  const { locale } = useLocale()
  const navigate = useNavigate()
  const {
    data: candidates,
    isLoading: isLoadingCandidates,
    error: errorCandidates
  } = useQuery(['grants-candidates', locale], grantsService.getCandidates, { locale })

  useEffect(() => {
    if (!isLoadingCandidates && !candidates?.length) {
      navigate(RoutePath.HOME)
    }
  }, [candidates, isLoadingCandidates, navigate])

  if (isLoadingCandidates) {
    return <Loader data-testid="loading" />
  }

  if (errorCandidates) {
    return <ErrorState description={'grants.error.description'} />
  }

  if (!candidates || candidates.length === 0) {
    return <EmptyState illustration="no-grant-programs" title={t('grants.noGrants.title')} />
  }

  if (!claimAccepted) {
    return (
      <Flex className={styles.grantsContainer}>
        <Typography variant="h1">{t('grants.title')}</Typography>
        <Alert variant="info">
          <Trans
            i18nKey="grants.noGrants.noClaim.alert"
            components={{ a: <a href={HELP_LINK} target="_blank" rel="noreferrer" /> }}
          />
        </Alert>
        <EmptyState
          illustration="no-grant-candidates"
          title={t('grants.noGrants.noClaim.title')}
          description={t('grants.noGrants.noClaim.description')}
        />
      </Flex>
    )
  }

  return (
    <Flex className={styles.grantsContainer}>
      <Flex direction="column" gap={4}>
        <Typography variant="h1">{t('grants.title')}</Typography>
        <Typography>{t('grants.description')}</Typography>
      </Flex>

      <Flex gap={24} wrap="wrap">
        {candidates.map(item => (
          <GrantProgramCard key={item.id} {...item} />
        ))}
      </Flex>
    </Flex>
  )
}
